import { BusyWatcher, watchBusy } from "@frui.ts/screens";
import { action, computed, observable } from "mobx";
import type LocalizationService from "services/localizationService";
import type AppRouter from "services/router";
import type PayGateRefundResponse from "entities/payGateRefundResponse";
import type RefundTransferPaymentResponse from "entities/refundTransferPaymentResponse";
import type EventBus from "services/eventBus";
import { notificationAdded } from "services/eventBus";
import { scrollModalTop } from "utils/scrollTop";
import type AlertsViewModel from "../../../alertsViewModel";
import DetailViewModel from "../../../detailViewModel";
import { Modules } from "config";
import type ChargeResponse from "entities/chargeResponse";
import type { IRefund } from "entities/types";
import type SenderBankAccount from "entities/refund/senderBankAccount";
import type RefundsApprovalService from "services/refundsApprovalService";
import { bound } from "@frui.ts/helpers";
import type RefundCounterpartyBankRequest from "entities/refundCounterpartyBankRequest";
import type { IPagedResults } from "data/types";
import type { IPagingFilter, IPagingInfo } from "@frui.ts/data";
import type TransferOriginalPaymentDetailResponse from "entities/transferOriginalPaymentDetailResponse";
import Approvable from "entities/approvable";
import type RefundForRefundOfRefundsRepository from "repositories/refundForRefundOfRefundsRepository";
import type OptionsService from "services/optionsService";
import Exportable from "entities/exportable";
import type { IWithPrevNextNavigationViewModel } from "viewModels/types";
import type RefundForRefundOfRefundPaymentResponse from "entities/refundForRefundOfRefundPaymentResponse";
import type TransferSourcePaymentDetailResponse from "entities/transferSourcePaymentDetailResponse";
import type RefundForRefundOfRefundDetailResponse from "entities/refundForRefundOfRefundDetailResponse";
import type EnumService from "../../../../services/enumService";
import type PriceListService from "../../../../services/priceListService";
import { translateChargeTypeByPriceListItem } from "utils/chargeType";

export default abstract class RefundForRefundOfRefundDetailViewModel<
    TRefund extends Approvable = Approvable,
    TRefundList extends Approvable = TRefund
  >
  extends DetailViewModel<TRefund>
  implements IWithPrevNextNavigationViewModel
{
  protected abstract fetchData(): Promise<IPagedResults<TRefundList>>;
  @observable busyWatcher: BusyWatcher = new BusyWatcher();
  protected unsubscribtions: Array<() => void> = [];

  @observable protected items?: TRefundList[];
  protected total: number;
  protected currentIndex: number;
  protected currentPaging: IPagingInfo;
  @observable protected itemId: string;

  @computed
  get name() {
    return this.translate("title");
  }

  constructor(
    itemId: string,
    protected pagingFilter: IPagingFilter,
    protected localizationService: LocalizationService,
    protected appRouter: AppRouter,
    protected approvalService: RefundsApprovalService,
    protected optionsService: OptionsService,
    protected repository: RefundForRefundOfRefundsRepository,
    protected eventBus: EventBus,
    protected priceListService: PriceListService,
    protected enumService: EnumService,
    public alertsViewModel: AlertsViewModel
  ) {
    super(itemId);
  }

  async onInitialize() {
    this.unsubscribtions.push(this.eventBus.subscribe(notificationAdded, this.onNotificationAdded));
    return super.onInitialize();
  }
  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscribtions.forEach(unsubscribe => unsubscribe());
      this.unsubscribtions = [];
    }

    return super.onDeactivate(isClosing);
  }

  @bound
  protected onNotificationAdded() {
    scrollModalTop();
  }

  @bound
  @watchBusy
  async loadList() {
    const result = await this.fetchData();

    const {
      metadata: { limit, offset, total: totalItems },
      items,
    } = result;
    this.total = totalItems;
    this.pagingFilter = { limit, offset };
    return items;
  }

  @computed
  get hasPrevious() {
    if (!this.items) {
      return false;
    }

    const index = this.items.findIndex(item => item.id === this.itemId);

    return !(index - 1 < 0 && this.pagingFilter.offset === 0);
  }

  @computed
  get hasNext() {
    if (!this.items) {
      return false;
    }

    const index = this.items.findIndex(item => item.id === this.itemId);
    const nextIndex = index + 1;

    return !(nextIndex >= this.items.length && nextIndex === this.total - this.pagingFilter.offset);
  }

  @bound
  async onPreviousClick() {
    if (!this.items) {
      return;
    }

    const index = this.items.findIndex(item => item.id === this.itemId);

    if (this.hasPrevious) {
      const previousIndex = index - 1;
      this.currentIndex = previousIndex < 0 ? this.pagingFilter.limit - 1 : previousIndex;
      this.itemId = this.items[this.currentIndex].id;
    }

    if (this.currentIndex === 0 && this.pagingFilter.offset - this.pagingFilter.limit >= 0) {
      this.pagingFilter.offset -= this.pagingFilter.limit;
      this.items = undefined;
    }

    this.navigationName = `${this.itemId}`;
    this.notifyNavigationChanged();

    await this.onInitialize();
  }

  private inNextLimitBoundary() {
    return this.currentIndex === this.pagingFilter.limit - 1 && this.pagingFilter.offset + this.pagingFilter.limit < this.total;
  }

  @bound
  async onNextClick() {
    if (!this.items) {
      return;
    }

    const index = this.items.findIndex(item => item.id === this.itemId);

    if (this.hasNext) {
      this.currentIndex = index + 1;
      this.itemId = this.items[this.currentIndex].id;
    }

    if (this.inNextLimitBoundary()) {
      this.pagingFilter.offset += this.pagingFilter.limit;
      this.items = undefined;
    }

    this.navigationName = `${this.itemId}`;
    this.notifyNavigationChanged();

    await this.onInitialize();
  }

  @bound
  onApproveClick(item: Approvable) {
    return async () => {
      await this.approvalService.approve(this.repository, item, {
        title: this.translate("confirmApproval.title"),
        confirm: this.translate("confirmApproval.confirm"),
        approved: this.translate("approved"),
      });
    };
  }

  @computed
  get allowApprove() {
    return this.approvalService.allowWrite();
  }

  @action.bound
  canApprove(item: Approvable) {
    if (item.permittedActions) {
      return item.permittedActions.approval.isAllowed && this.approvalService.canApprove(item);
    }

    return this.approvalService.canApprove(item);
  }

  @bound
  approvedByMe(item: Approvable) {
    return this.approvalService.approvedByMe(item);
  }

  @bound
  isExported(item: Exportable) {
    return false;
  }

  @bound
  myApprovalStep(item: Approvable) {
    return this.approvalService.myApprovalStep(item);
  }

  @bound
  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `refundApprovals.detail.${code}`, size);
  }

  @bound
  translateModuleGeneral(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `refundApprovals.${code}`, size);
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  countryCode(countryId: string) {
    return this.approvalService.countryCode(countryId);
  }

  chargeType(priceListItemId?: string) {
    return translateChargeTypeByPriceListItem(this.priceListService, this.enumService, priceListItemId);
  }

  translateModelAttribute(model: "charge", attribute: keyof ChargeResponse, size?: number): string;
  translateModelAttribute(model: "refund", attribute: keyof IRefund, size?: number): string;
  translateModelAttribute(model: "merchantBankAccount", attribute: keyof SenderBankAccount, size?: number): string;
  translateModelAttribute(
    model: "refundCounterpartyBankRequest",
    attribute: keyof RefundCounterpartyBankRequest,
    size?: number
  ): string;
  translateModelAttribute(model: "senderBankAccount", attribute: keyof SenderBankAccount, size?: number): string;
  translateModelAttribute(model: "payGateRefund", attribute: keyof PayGateRefundResponse, size?: number): string;
  translateModelAttribute(model: "transferRefundPayment", attribute: keyof RefundTransferPaymentResponse, size?: number): string;
  translateModelAttribute(
    model: "transferOriginalPayment",
    attribute: keyof TransferOriginalPaymentDetailResponse,
    size?: number
  ): string;
  translateModelAttribute(
    model: "refundForRefundOfRefund",
    attribute: keyof RefundForRefundOfRefundDetailResponse,
    size?: number
  ): string;
  translateModelAttribute(
    model: "refundForRefundOfRefundPayment",
    attribute: keyof RefundForRefundOfRefundPaymentResponse,
    size?: number
  ): string;
  translateModelAttribute(
    model: "transferSourcePaymentDetail",
    attribute: keyof TransferSourcePaymentDetailResponse,
    size?: number
  ): string;
  translateModelAttribute(
    model: // tslint:disable-next-line:max-union-size
    | "charge"
      | "refund"
      | "merchantBankAccount"
      | "refundCounterpartyBankRequest"
      | "senderBankAccount"
      | "payGateRefund"
      | "transferRefundPayment"
      | "transferOriginalPayment"
      | "refundForRefundOfRefund"
      | "refundForRefundOfRefundPayment"
      | "transferSourcePaymentDetail",
    attribute: // tslint:disable-next-line:max-union-size
    | keyof ChargeResponse
      | keyof IRefund
      | keyof SenderBankAccount
      | keyof RefundCounterpartyBankRequest
      | keyof PayGateRefundResponse
      | keyof RefundTransferPaymentResponse
      | keyof TransferOriginalPaymentDetailResponse
      | keyof RefundForRefundOfRefundDetailResponse
      | keyof RefundForRefundOfRefundPaymentResponse
      | keyof TransferSourcePaymentDetailResponse,
    size?: number
  ) {
    return this.localizationService.translateAttribute(model, attribute, size);
  }
}
